import { useEffect, useState } from "react";
import { Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { toUnit, distUnit } from "@/lib/speedo";

const hms = (s) => { s = Math.round(s); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m ${s % 60}s`; };

function Route({ path }) {
  if (!path || path.length < 2) return <div className="h-24 rounded-2xl bg-white/5 flex items-center justify-center text-xs text-white/40"><MapPin className="w-4 h-4 mr-1" />No route recorded</div>;
  const lats = path.map((p) => p[0]), lons = path.map((p) => p[1]);
  const [a, b, c, d] = [Math.min(...lats), Math.max(...lats), Math.min(...lons), Math.max(...lons)];
  const sx = (lon) => 10 + ((lon - c) / (d - c || 1e-9)) * 280, sy = (lat) => 90 - ((lat - a) / (b - a || 1e-9)) * 80;
  return (
    <svg viewBox="0 0 300 100" className="w-full h-24 rounded-2xl bg-white/5" data-testid="speedo-trip-route">
      <polyline points={path.map((p) => `${sx(p[1])},${sy(p[0])}`).join(" ")} fill="none" stroke="#39FF14" strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx={sx(path[0][1])} cy={sy(path[0][0])} r="4" fill="#00F0FF" /><circle cx={sx(path.at(-1)[1])} cy={sy(path.at(-1)[0])} r="4" fill="#FF2BD6" />
    </svg>
  );
}

export default function SpeedoTrips({ unit }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/speedo/trips").then((r) => setData(r.data)).catch(() => setData({ trips: [], totals: {} }));
  useEffect(() => { load(); }, []);
  const expand = async (t) => {
    if (open?.id === t.id) return setOpen(null);
    const { data: d } = await api.get(`/speedo/trips/${t.id}`); setOpen(d.trip);
  };
  const del = async (t) => {
    if (!window.confirm("Delete this trip?")) return;
    try { await api.delete(`/speedo/trips/${t.id}`); toast("Trip deleted"); setOpen(null); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const du = unit === "kmh" ? "km" : "mi", su = unit === "kmh" ? "km/h" : "mph";
  if (!data) return <div className="text-white/40 animate-pulse">Loading trips…</div>;
  return (
    <div data-testid="speedo-trips">
      <div className="grid grid-cols-3 gap-2 mb-6">
        {[["Trips", data.totals.trips || 0], [`Total ${du}`, distUnit(data.totals.distance_m || 0, unit).toFixed(1)], [`Top ${su}`, Math.round(toUnit(data.totals.max_speed_mps || 0, unit))]].map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white/[0.04] border border-white/10 p-3"><div className="mono text-[10px] uppercase tracking-widest text-white/40">{k}</div><div className="font-heading font-black text-2xl" data-testid={`speedo-total-${k.split(" ")[0].toLowerCase()}`}>{v}</div></div>
        ))}
      </div>
      {!data.trips.length ? <div className="py-12 text-center text-white/50 text-sm" data-testid="speedo-trips-empty">No saved trips yet. Start a trip on the Drive tab — it saves here when you stop.</div> : (
        <div className="space-y-2">
          {data.trips.map((t) => (
            <div key={t.id} className="rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`speedo-trip-${t.id}`}>
              <button onClick={() => expand(t)} className="w-full text-left p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{t.name}</div>
                  <div className="text-xs text-white/50 mono">{new Date(t.started_at).toLocaleString()} · {hms(t.duration_s)}</div>
                </div>
                <div className="text-right"><div className="font-heading font-black text-xl">{distUnit(t.distance_m, unit).toFixed(2)} <span className="text-xs text-white/50">{du}</span></div>
                  <div className="text-[11px] text-white/50 mono">top {Math.round(toUnit(t.max_speed_mps, unit))} {su}</div></div>
              </button>
              {open?.id === t.id && (
                <div className="px-4 pb-4 space-y-3">
                  <Route path={open.path} />
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div><div className="text-white/40">Avg</div><b>{Math.round(toUnit(t.avg_speed_mps, unit))} {su}</b></div>
                    <div><div className="text-white/40">Moving</div><b>{hms(t.moving_s || 0)}</b></div>
                    <div><div className="text-white/40">Over target</div><b>{hms(t.over_target_s || 0)}</b></div>
                  </div>
                  <button onClick={() => del(t)} className="text-xs text-[#FF003C] flex items-center gap-1" data-testid={`speedo-trip-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete trip</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
